import React, { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Label,
  SpinnerLoader,
  Toastify,
  DatePicker,
  SingleSelect,
} from 'lift-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { useActivateConfiguration } from '../../hooks';
import type { ReportConfiguration } from '../../types';
import { transformToUI } from '../../utils/valueTransformations';

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  validate: (value: string) => boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, options, placeholder, validate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (val === '' || validate(val)) {
      onChange(val);
    }
  };

  const handleBlur = () => {
    if (value && value.length === 1) {
      onChange(value.padStart(2, '0'));
    }
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-[50px] p-2 border border-r-0 border-gray-300 rounded-l-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          maxLength={2}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="px-1 border border-gray-300 rounded-r-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </button>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-[70px] max-h-[200px] overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => handleSelect(option)}
              className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-primary hover:text-primary-foreground ${
                value === option ? 'bg-primary text-primary-foreground' : ''
              }`}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface ActivationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  config: ReportConfiguration;
  brandId: number;
  brandName?: string;
  onSuccess: () => void;
}

const ActivationDialog: React.FC<ActivationDialogProps> = ({
  isOpen,
  onClose,
  config,
  brandId,
  brandName,
  onSuccess,
}) => {
  const now = new Date();
  const currentDeactivationDate = config.deactivation_at ? new Date(config.deactivation_at) : null;
  const existingReactivationDate = config.reactivation_at ? new Date(config.reactivation_at) : null;
  const isAlreadyDeactivated = currentDeactivationDate ? currentDeactivationDate <= now : false;
  const isScheduledForFuture = currentDeactivationDate ? currentDeactivationDate > now : false;
  
  // Check if there's already a future reactivation scheduled
  const hasExistingFutureReactivation = existingReactivationDate ? existingReactivationDate > now : false;

  // Helper to extract time from a date
  const getTimeFromDate = (date: Date) => {
    const hour24 = date.getHours();
    const hour12 = hour24 % 12 || 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    return {
      hour: String(hour12).padStart(2, '0'),
      minute: String(date.getMinutes()).padStart(2, '0'),
      period,
    };
  };

  // Default to current time + 1 hour, rounded to nearest 15 min
  const getDefaultTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    const hour24 = now.getHours();
    const hour12 = hour24 % 12 || 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    return {
      hour: String(hour12).padStart(2, '0'),
      minute: String(minutes % 60).padStart(2, '0'),
      period,
    };
  };

  // Initialize time from existing reactivation date or default
  const initialTime = hasExistingFutureReactivation && existingReactivationDate
    ? getTimeFromDate(existingReactivationDate)
    : getDefaultTime();

  // If there's an existing reactivation, default to schedule_reactivation action
  const [actionType, setActionType] = useState<'cancel' | 'schedule_reactivation'>(
    hasExistingFutureReactivation ? 'schedule_reactivation' : 'cancel'
  );
  const [reactivationDate, setReactivationDate] = useState<Date | undefined>(
    hasExistingFutureReactivation && existingReactivationDate ? existingReactivationDate : undefined
  );

  const [selectedHour, setSelectedHour] = useState(initialTime.hour);
  const [selectedMinute, setSelectedMinute] = useState(initialTime.minute);
  const [selectedPeriod, setSelectedPeriod] = useState(initialTime.period);

  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  const validateHour = (val: string) => {
    const num = parseInt(val, 10);
    return num >= 1 && num <= 12;
  };

  const validateMinute = (val: string) => {
    const num = parseInt(val, 10);
    return num >= 0 && num <= 59;
  };

  const getScheduledTime = () => {
    let hour24 = parseInt(selectedHour, 10);
    if (selectedPeriod === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (selectedPeriod === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    return `${String(hour24).padStart(2, '0')}:${selectedMinute}`;
  };

  const { mutate: activate, isPending } = useActivateConfiguration();

  const handleSubmit = () => {
    // Handle case: Already deactivated with future reactivation scheduled
    if (isAlreadyDeactivated && hasExistingFutureReactivation) {
      if (actionType === 'cancel') {
        // Activate immediately (clear reactivation schedule)
        activate(
          {
            id: config.id,
            brand_id: brandId,
            brand_name: brandName,
            payload: {},
          },
          {
            onSuccess: () => {
              Toastify('success', 'Configuration activated successfully');
              onSuccess();
            },
            onError: (error: Error) => {
              Toastify('error', error.message || 'Failed to activate configuration');
            },
          }
        );
      } else {
        // Update reactivation date
        if (!reactivationDate) {
          Toastify('error', 'Please select a reactivation date');
          return;
        }

        if (!selectedHour || !selectedMinute || !selectedPeriod) {
          Toastify('error', 'Please select a time');
          return;
        }

        // Combine date and time
        const scheduledTime = getScheduledTime();
        const [hours, minutes] = scheduledTime.split(':');
        const combinedDateTime = new Date(reactivationDate);
        combinedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

        // Validate reactivation date is after current deactivation_at
        if (currentDeactivationDate && combinedDateTime <= currentDeactivationDate) {
          Toastify('error', 'Reactivation date must be after the deactivation date');
          return;
        }

        activate(
          {
            id: config.id,
            brand_id: brandId,
            brand_name: brandName,
            payload: { reactivation_at: combinedDateTime.toISOString() },
          },
          {
            onSuccess: () => {
              Toastify('success', 'Reactivation schedule updated successfully');
              onSuccess();
            },
            onError: (error: Error) => {
              Toastify('error', error.message || 'Failed to update reactivation schedule');
            },
          }
        );
      }
    } else if (isAlreadyDeactivated) {
      // Simple reactivation for already deactivated entries (no future reactivation)
      activate(
        {
          id: config.id,
          brand_id: brandId,
          brand_name: brandName,
          payload: {},
        },
        {
          onSuccess: () => {
            Toastify('success', 'Configuration activated successfully');
            onSuccess();
          },
          onError: (error: Error) => {
            Toastify('error', error.message || 'Failed to activate configuration');
          },
        }
      );
    } else if (isScheduledForFuture) {
      if (actionType === 'cancel') {
        // Cancel scheduled deactivation
        activate(
          {
            id: config.id,
            brand_id: brandId,
            brand_name: brandName,
            payload: { cancel_scheduled: true },
          },
          {
            onSuccess: () => {
              Toastify('success', 'Scheduled deactivation cancelled successfully');
              onSuccess();
            },
            onError: (error: Error) => {
              Toastify('error', error.message || 'Failed to cancel scheduled deactivation');
            },
          }
        );
      } else {
        // Schedule reactivation for a date AFTER deactivation
        if (!reactivationDate) {
          Toastify('error', 'Please select a reactivation date');
          return;
        }

        if (!selectedHour || !selectedMinute || !selectedPeriod) {
          Toastify('error', 'Please select a time');
          return;
        }

        // Combine date and time
        const scheduledTime = getScheduledTime();
        const [hours, minutes] = scheduledTime.split(':');
        const combinedDateTime = new Date(reactivationDate);
        combinedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

        // Validate reactivation date is after current deactivation_at
        if (currentDeactivationDate && combinedDateTime <= currentDeactivationDate) {
          Toastify('error', 'Reactivation date must be after the deactivation date');
          return;
        }

        activate(
          {
            id: config.id,
            brand_id: brandId,
            brand_name: brandName,
            payload: { reactivation_at: combinedDateTime.toISOString() },
          },
          {
            onSuccess: () => {
              Toastify('success', hasExistingFutureReactivation 
                ? 'Reactivation schedule updated successfully' 
                : 'Reactivation scheduled successfully');
              onSuccess();
            },
            onError: (error: Error) => {
              Toastify('error', error.message || 'Failed to schedule reactivation');
            },
          }
        );
      }
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Activate Configuration</SheetTitle>
          <SheetDescription>
            {isAlreadyDeactivated
              ? 'Reactivate this configuration to resume data fetches.'
              : 'Choose whether to cancel the scheduled deactivation or schedule a reactivation date after deactivation occurs.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Configuration Summary */}
          <div className="p-3 bg-gray-50 rounded-md space-y-1 text-sm">
            <div><strong>Platform:</strong> {transformToUI(config.platform)}</div>
            <div><strong>Report Type:</strong> {transformToUI(config.report_type)}</div>
            <div><strong>Granularity:</strong> {transformToUI(config.granularity_level)}</div>
            {config.cadence !== 'n/a' && (
              <div><strong>Cadence:</strong> {transformToUI(config.cadence)}</div>
            )}
            {config.metric_type !== 'n/a' && (
              <div><strong>Metric Type:</strong> {transformToUI(config.metric_type)}</div>
            )}
            {currentDeactivationDate && (
              <div className="mt-2 pt-2 border-t">
                <strong>Current Deactivation Date:</strong>{' '}
                {currentDeactivationDate.toLocaleString()}
              </div>
            )}
            {existingReactivationDate && (
              <div className="mt-1">
                <strong>Current Reactivation Date:</strong>{' '}
                {existingReactivationDate.toLocaleString()}
              </div>
            )}
          </div>

          {/* Options for scheduled entries OR already deactivated with future reactivation */}
          {(isScheduledForFuture || (isAlreadyDeactivated && hasExistingFutureReactivation)) && (
            <div className="space-y-3">
              {/* Info message when editing existing reactivation */}
              {hasExistingFutureReactivation && isAlreadyDeactivated && (
                <div className="p-3 bg-blue-50 rounded-md text-sm text-blue-900">
                  This entry is scheduled to reactivate on {existingReactivationDate?.toLocaleString()}. 
                  You can update the reactivation date or activate immediately.
                </div>
              )}
              {hasExistingFutureReactivation && !isAlreadyDeactivated && (
                <div className="p-3 bg-blue-50 rounded-md text-sm text-blue-900">
                  Update the scheduled reactivation date and time below, or cancel the scheduled deactivation entirely.
                </div>
              )}

              <Label>Choose Action</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cancel"
                    checked={actionType === 'cancel'}
                    onCheckedChange={(checked) => {
                      if (checked) setActionType('cancel');
                    }}
                  />
                  <Label htmlFor="cancel" className="cursor-pointer font-normal">
                    {isAlreadyDeactivated 
                      ? 'Activate immediately (clear reactivation schedule)' 
                      : 'Cancel scheduled deactivation (activate immediately)'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="schedule_reactivation"
                    checked={actionType === 'schedule_reactivation'}
                    onCheckedChange={(checked) => {
                      if (checked) setActionType('schedule_reactivation');
                    }}
                  />
                  <Label htmlFor="schedule_reactivation" className="cursor-pointer font-normal">
                    {hasExistingFutureReactivation ? 'Update reactivation date' : 'Schedule reactivation date'}
                  </Label>
                </div>
              </div>

              {/* Date picker for schedule reactivation option */}
              {actionType === 'schedule_reactivation' && (
                <div className="space-y-3 pl-6">
                  <div className="space-y-1.5">
                    <Label>Reactivation Date *</Label>
                    <DatePicker
                      value={reactivationDate}
                      onChange={(date: Date) => setReactivationDate(date)}
                      placeholder="Select reactivation date"
                      disabled={false}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500">
                      Must be after {currentDeactivationDate?.toLocaleDateString()}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Time *</Label>
                    <div className="flex items-center gap-2">
                      <TimeInput
                        value={selectedHour}
                        onChange={setSelectedHour}
                        options={hourOptions}
                        placeholder="HH"
                        validate={validateHour}
                      />
                      <span className="text-lg font-medium">:</span>
                      <TimeInput
                        value={selectedMinute}
                        onChange={setSelectedMinute}
                        options={minuteOptions}
                        placeholder="MM"
                        validate={validateMinute}
                      />
                      <SingleSelect
                        options={[
                          { label: 'AM', value: 'AM' },
                          { label: 'PM', value: 'PM' },
                        ]}
                        defaultValue={selectedPeriod}
                        onChange={setSelectedPeriod}
                        placeholder="AM"
                        containerClassName="w-[120px]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Simple message for already deactivated (no reactivation scheduled) */}
          {isAlreadyDeactivated && !hasExistingFutureReactivation && (
            <div className="p-3 bg-blue-50 rounded-md text-sm text-blue-900">
              This configuration is currently deactivated. Clicking "Activate" will immediately resume data fetches.
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <SpinnerLoader className="h-4 w-4 mr-2" />
                Processing...
              </>
            ) : (isAlreadyDeactivated && !hasExistingFutureReactivation) ? (
              'Activate Now'
            ) : actionType === 'cancel' ? (
              isAlreadyDeactivated ? 'Activate Now' : 'Cancel Deactivation'
            ) : hasExistingFutureReactivation ? (
              'Update Reactivation'
            ) : (
              'Schedule Reactivation'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ActivationDialog;
